import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from './firebase';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import RecaptchaComponent from './components/RecaptchaComponent';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { createUser, getUser } from './services/userService';
import { useTranslation } from 'react-i18next';
import SEO from './components/SEO';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');

    try {
      // Execute reCAPTCHA verification
      if (recaptchaRef.current && recaptchaRef.current.executeRecaptcha) {
        const token = await recaptchaRef.current.executeRecaptcha();
        if (!token) {
          setError(t('errors.recaptchaVerificationFailed'));
          setLoading(false);
          return;
        }
      }

      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      
      // Check if email is verified
      if (!user.emailVerified) {
        // Redirect to email verification page
        navigate('/verify-email', { 
          state: { 
            email: user.email,
            displayName: user.displayName 
          } 
        });
        return;
      }
      
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/user-not-found':
          setError(t('errors.noAccountFound'));
          break;
        case 'auth/wrong-password':
          setError(t('errors.incorrectPassword'));
          break;
        case 'auth/invalid-email':
          setError(t('errors.invalidEmail'));
          break;
        case 'auth/user-disabled':
          setError(t('errors.accountDisabled'));
          break;
        case 'auth/too-many-requests':
          setError(t('errors.tooManyRequests'));
          break;
        default:
          setError(t('errors.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptchaVerify = () => {
    setRecaptchaError('');
    // Clear any existing error when reCAPTCHA is verified
    if (error && error.includes('reCAPTCHA')) {
      setError('');
    }
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaError(error);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Check if user document exists in Firestore
      const userDoc = await getUser(user.uid);
      
      // If user doesn't exist in Firestore, create a new user document
      if (!userDoc) {
        await createUser(user.uid, user.email, {
          displayName: user.displayName || null,
          emailVerified: user.emailVerified
        });
      }

      // Navigate to home page
      navigate('/');
    } catch (err) {
      console.error('Google sign-in error:', err);
      
      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/popup-closed-by-user':
          setError(t('errors.popupClosed'));
          break;
        case 'auth/popup-blocked':
          setError(t('errors.popupBlocked'));
          break;
        case 'auth/account-exists-with-different-credential':
          setError(t('errors.accountExistsDifferentCredential'));
          break;
        case 'auth/auth-domain-config-required':
          setError(t('errors.googleSignInNotConfigured'));
          break;
        default:
          setError(t('errors.googleSignInFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign In to Osailu"
        description="Sign in to your Osailu account to access automation software for virtual learning environments. Secure login with email or Google authentication."
        keywords="Osailu login, sign in, Osailu account, virtual learning platform"
        url="/login"
        noindex={true}
      />
      <div style={{ 
        minHeight: 'calc(100vh - 120px)', 
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-color)',
        transition: 'background-color 0.3s ease, color 0.3s ease',
        padding: '20px 0'
      }}>
      <div className="container mt-5">
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card>
            <Card.Header className="text-center">
              <h3>{t('auth.welcomeBack')}</h3>
              <p className="text-muted mb-0">{t('auth.signInToAccount')}</p>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleLogin}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.emailAddress')}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('auth.enterEmail')}
                    required
                    autoComplete="email"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.password')}</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('auth.enterPassword')}
                    required
                    autoComplete="current-password"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <RecaptchaComponent
                    ref={recaptchaRef}
                    onVerify={handleRecaptchaVerify}
                    onError={handleRecaptchaError}
                    siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  />
                  {recaptchaError && (
                    <div className="text-danger small mt-1">
                      {recaptchaError}
                    </div>
                  )}
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mb-3"
                  disabled={loading}
                >
                  {loading ? t('auth.signingIn') : t('auth.signIn')}
                </Button>
              </Form>

              <div className="text-center mb-3">
                <div className="position-relative">
                  <hr />
                  <span className="position-absolute top-50 start-50 translate-middle px-2 text-muted small" style={{ backgroundColor: 'var(--bg-color)' }}>
                    {t('common.or')}
                  </span>
                </div>
              </div>

              <Button
                variant="outline-primary"
                className="w-100 mb-3"
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <g fill="#000" fillRule="evenodd">
                    <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
                    <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.18-1.79 2.91l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.61z" fill="#4285F4"/>
                    <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
                    <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
                  </g>
                </svg>
                {loading ? t('auth.signingIn') : t('auth.continueWithGoogle')}
              </Button>

              <div className="text-center">
                <p className="mb-2">
                  <Link to="/forgot-password" className="text-decoration-none">
                    {t('auth.forgotPassword')}
                  </Link>
                </p>
                <p className="mb-0">
                  {t('auth.dontHaveAccount')}{' '}
                  <Link to="/signup" className="text-decoration-none">
                    {t('auth.createOneHere')}
                  </Link>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
    </div>
    </>
  );
};

const LoginWithRecaptcha = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  if (!recaptchaSiteKey) {
    console.warn('reCAPTCHA site key not configured');
    return <Login />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <Login />
    </GoogleReCaptchaProvider>
  );
};

export default LoginWithRecaptcha;
